import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, CheckCircle2, Mail, Hash, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ZelleInstructions({ zelle, refId, purpose = "order", onDone, cancellationPolicy = null }) {
  const [copied, setCopied] = useState({});

  const copy = (key, value) => {
    navigator.clipboard.writeText(String(value));
    setCopied((c) => ({ ...c, [key]: true }));
    toast.success("Copied");
    setTimeout(() => setCopied((c) => ({ ...c, [key]: false })), 1500);
  };

  const Row = ({ label, value, icon: Icon, tKey }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-zinc-100 last:border-0">
      <div className="flex items-center gap-2 text-zinc-600 text-sm">
        <Icon className="w-4 h-4 text-[#0C2E2B]" />
        {label}
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono-data font-bold text-[#0C2E2B] truncate max-w-[220px]">{value}</span>
        <button
          type="button"
          data-testid={`zelle-copy-${tKey}-btn`}
          onClick={() => copy(tKey, value)}
          className="text-zinc-500 hover:text-[#0C2E2B]"
        >
          {copied[tKey] ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="zelle-instructions" className="space-y-4">
      <div className="text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-[#FFDF00] grid place-items-center">
          <DollarSign className="w-7 h-7 text-[#0C2E2B]" strokeWidth={2.5} />
        </div>
        <h3 className="font-display font-black text-2xl text-[#0C2E2B] mt-3">One quick payment left</h3>
        <p className="text-sm text-zinc-500 mt-1">Send by Zelle to lock this {purpose}.</p>
      </div>

      <div className="bg-zinc-50 border-2 border-[#0C2E2B] rounded-lg p-4">
        <Row label="Recipient" value={zelle.recipient_name} icon={CheckCircle2} tKey="name" />
        <Row label="Zelle email" value={zelle.email} icon={Mail} tKey="email" />
        <Row label="Amount" value={`$${Number(zelle.amount).toFixed(2)}`} icon={DollarSign} tKey="amount" />
        <Row label="Memo / Reference" value={zelle.memo || refId} icon={Hash} tKey="memo" />
      </div>

      <div className="bg-[#FFDF00]/20 border-l-4 border-[#FFDF00] p-3 rounded-md text-xs text-zinc-800">
        <b>Include the memo</b> "<span className="font-mono-data">{zelle.memo || refId}</span>" so we can match your payment to this {purpose}. We'll email you once it lands.
      </div>

      {cancellationPolicy && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-md text-xs text-red-900" data-testid="cancellation-policy">
          <b>Deposit policy:</b> {cancellationPolicy}
        </div>
      )}

      {onDone && (
        <Button data-testid="zelle-done-btn" onClick={onDone} className="btn-kinetic rounded-md w-full py-5">
          I've sent the payment
        </Button>
      )}
    </div>
  );
}
